import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Plus, Trash2, Save, FileText, Loader2, Sparkles, BookOpen, Scale,
  CheckCircle2, RotateCcw, Search, TrendingUp, Building2, Lock, Unlock,
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

const ACCOUNT_TYPES = [
  { value: 'asset',     label: 'Asset',     color: 'bg-emerald-100 text-emerald-700' },
  { value: 'liability', label: 'Liability', color: 'bg-rose-100 text-rose-700' },
  { value: 'equity',    label: 'Equity',    color: 'bg-violet-100 text-violet-700' },
  { value: 'revenue',   label: 'Revenue',   color: 'bg-sky-100 text-sky-700' },
  { value: 'expense',   label: 'Expense',   color: 'bg-amber-100 text-amber-700' },
];

const fmtMoney = (v) => `GHS ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LedgerPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = tab || 'accounts';

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">General Ledger</h1>
          <p className="text-sm text-muted-foreground">
            Chart of accounts, double-entry journals and trial balance
          </p>
        </div>
      </div>

      <Tabs value={active} onValueChange={(v) => navigate(`/finance/ledger/${v}`)} className="space-y-4">
        <TabsList className="grid grid-cols-6 max-w-4xl">
          <TabsTrigger value="accounts" data-testid="tab-accounts">
            <BookOpen className="h-4 w-4 mr-1.5" /> Accounts
          </TabsTrigger>
          <TabsTrigger value="journals" data-testid="tab-journals">
            <FileText className="h-4 w-4 mr-1.5" /> Journals
          </TabsTrigger>
          <TabsTrigger value="trial-balance" data-testid="tab-trial-balance">
            <Scale className="h-4 w-4 mr-1.5" /> Trial Balance
          </TabsTrigger>
          <TabsTrigger value="profit-loss" data-testid="tab-profit-loss">
            <TrendingUp className="h-4 w-4 mr-1.5" /> P&amp;L
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" data-testid="tab-balance-sheet">
            <Building2 className="h-4 w-4 mr-1.5" /> Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="period-close" data-testid="tab-period-close">
            <Lock className="h-4 w-4 mr-1.5" /> Period Close
          </TabsTrigger>
        </TabsList>

        {active === 'accounts' && <AccountsTab />}
        {active === 'journals' && <JournalsTab />}
        {active === 'trial-balance' && <TrialBalanceTab />}
        {active === 'profit-loss' && <ProfitLossTab />}
        {active === 'balance-sheet' && <BalanceSheetTab />}
        {active === 'period-close' && <PeriodCloseTab />}
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Accounts tab
// ─────────────────────────────────────────────────────────────────────

function AccountsTab() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [seeding, setSeeding] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ledger/accounts');
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/ledger/accounts/seed');
      toast.success(`Inserted ${data.inserted} accounts (skipped ${data.skipped})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const filtered = useMemo(() => {
    return (accounts || []).filter((a) => {
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      if (filter) {
        const q = filter.toLowerCase();
        return (a.code || '').toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [accounts, filter, typeFilter]);

  const grouped = useMemo(() => {
    const out = { asset: [], liability: [], equity: [], revenue: [], expense: [] };
    for (const a of filtered) {
      if (out[a.type]) out[a.type].push(a);
    }
    return out;
  }, [filtered]);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Chart of Accounts</CardTitle>
          <CardDescription>
            {accounts.length === 0
              ? 'No accounts yet — start with the Ghana pharmacy template.'
              : `${accounts.length} accounts · GHS base currency`}
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {accounts.length === 0 ? (
            <Button onClick={seed} disabled={seeding} className="gap-1.5" data-testid="seed-coa-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Seed Ghana Pharmacy CoA
            </Button>
          ) : (
            <Button variant="outline" onClick={seed} disabled={seeding} className="gap-1.5" data-testid="seed-coa-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Refresh seed
            </Button>
          )}
          <Button onClick={() => setOpenCreate(true)} className="gap-1.5" data-testid="add-account-btn">
            <Plus className="h-4 w-4" /> Add account
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {accounts.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code or name…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-8"
                data-testid="coa-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="coa-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {ACCOUNT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border border-dashed rounded-lg">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No accounts in this tenant yet.</p>
            <p className="text-xs mt-1">Click <strong>Seed Ghana Pharmacy CoA</strong> to install a 53-account template.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {ACCOUNT_TYPES.map((t) => {
              const rows = grouped[t.value] || [];
              if (rows.length === 0) return null;
              return (
                <div key={t.value} data-testid={`coa-group-${t.value}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={t.color}>{t.label}</Badge>
                    <span className="text-xs text-muted-foreground">{rows.length} accounts</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2 w-24">Code</th>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2 w-44">Category</th>
                          <th className="text-left px-3 py-2 w-24">Currency</th>
                          <th className="text-left px-3 py-2 w-20">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((a) => (
                          <tr key={a.account_id} className="border-t hover:bg-muted/20" data-testid={`acc-row-${a.code}`}>
                            <td className="px-3 py-2 font-mono">{a.code}</td>
                            <td className="px-3 py-2">{a.name}{a.is_system ? <Badge variant="outline" className="ml-2 text-[9px]">SYS</Badge> : null}</td>
                            <td className="px-3 py-2 text-muted-foreground capitalize">{(a.category || '').replace(/_/g, ' ')}</td>
                            <td className="px-3 py-2 text-muted-foreground">{a.currency || 'GHS'}</td>
                            <td className="px-3 py-2">
                              {a.is_active === false
                                ? <Badge variant="outline" className="text-rose-700">Inactive</Badge>
                                : <Badge variant="outline" className="text-emerald-700">Active</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <CreateAccountDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
    </Card>
  );
}

function CreateAccountDialog({ open, onClose, onCreated }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('asset');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setCode(''); setName(''); setType('asset'); setCategory(''); }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/ledger/accounts', { code, name, type, category });
      toast.success('Account created');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-account-dialog">
        <DialogHeader>
          <DialogTitle>New account</DialogTitle>
          <DialogDescription>Add a custom account to your chart.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 1080" data-testid="acc-code" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Petty cash – branch 2" data-testid="acc-name" />
          </div>
          <div>
            <Label>Category (optional)</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. current_asset" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !code.trim() || !name.trim()} className="gap-1.5" data-testid="acc-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Journals tab
// ─────────────────────────────────────────────────────────────────────

function JournalsTab() {
  const [loading, setLoading] = useState(true);
  const [journals, setJournals] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [open, setOpen] = useState(null); // active journal for detail

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [jRes, aRes] = await Promise.all([
        api.get('/ledger/journals', { params }),
        api.get('/ledger/accounts'),
      ]);
      setJournals(jRes.data.journals || []);
      setAccounts(aRes.data.accounts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load journals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const post = async (id) => {
    try {
      await api.post(`/ledger/journals/${id}/post`);
      toast.success('Journal posted');
      load();
      setOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Post failed');
    }
  };

  const reverse = async (id) => {
    const reason = window.prompt('Reason for reversal (optional)') || '';
    try {
      await api.post(`/ledger/journals/${id}/reverse`, { reason });
      toast.success('Reversal posted');
      load();
      setOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reverse failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Journal Entries</CardTitle>
          <CardDescription>
            {journals.length} entries · double-entry validated
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="journal-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setOpenCreate(true)} className="gap-1.5" disabled={accounts.length === 0} data-testid="new-journal-btn">
            <Plus className="h-4 w-4" /> New journal
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {accounts.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
            Set up your Chart of Accounts first.
          </div>
        ) : loading ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : journals.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">No journals yet.</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-32">Entry #</th>
                  <th className="text-left px-3 py-2 w-28">Date</th>
                  <th className="text-left px-3 py-2">Memo</th>
                  <th className="text-left px-3 py-2 w-24">Source</th>
                  <th className="text-right px-3 py-2 w-32">Total</th>
                  <th className="text-left px-3 py-2 w-24">Status</th>
                  <th className="text-right px-3 py-2 w-20" />
                </tr>
              </thead>
              <tbody>
                {journals.map((j) => (
                  <tr key={j.journal_id} className="border-t hover:bg-muted/20" data-testid={`journal-row-${j.entry_no}`}>
                    <td className="px-3 py-2 font-mono">{j.entry_no}</td>
                    <td className="px-3 py-2">{j.date}</td>
                    <td className="px-3 py-2">{j.memo || <span className="text-muted-foreground italic">—</span>}</td>
                    <td className="px-3 py-2 text-muted-foreground capitalize">{j.source || 'manual'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(j.total)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(j)} data-testid={`open-journal-${j.entry_no}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <CreateJournalDialog open={openCreate} accounts={accounts} onClose={() => setOpenCreate(false)} onCreated={load} />
      <JournalDetailDialog journal={open} accounts={accounts} onClose={() => setOpen(null)} onPost={post} onReverse={reverse} />
    </Card>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-primary/5 border-primary/30' : ''}`}>
      <p className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</p>
      <p className={`text-base font-semibold tabular-nums mt-1 ${highlight ? 'text-primary' : ''}`}>{value}</p>
    </div>
  );
}


function StatusBadge({ status }) {
  if (status === 'posted') return <Badge className="bg-emerald-100 text-emerald-700">Posted</Badge>;
  if (status === 'draft')  return <Badge className="bg-amber-100 text-amber-700">Draft</Badge>;
  if (status === 'reversed') return <Badge className="bg-slate-200 text-slate-700">Reversed</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function CreateJournalDialog({ open, accounts, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState([
    { uid: crypto.randomUUID(), account_id: '', debit: '', credit: '', description: '' },
    { uid: crypto.randomUUID(), account_id: '', debit: '', credit: '', description: '' },
  ]);
  const [autoPost, setAutoPost] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(new Date().toISOString().slice(0, 10));
      setMemo('');
      setLines([
        { uid: crypto.randomUUID(), account_id: '', debit: '', credit: '', description: '' },
        { uid: crypto.randomUUID(), account_id: '', debit: '', credit: '', description: '' },
      ]);
      setAutoPost(false);
    }
  }, [open]);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines((ls) => [...ls, { uid: crypto.randomUUID(), account_id: '', debit: '', credit: '', description: '' }]);
  const removeLine = (idx) => setLines((ls) => ls.length > 2 ? ls.filter((_, i) => i !== idx) : ls);

  const totalDr = lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCr = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.005 && totalDr > 0;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        date,
        memo,
        auto_post: autoPost,
        lines: lines
          .filter((l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map((l) => ({
            account_id: l.account_id,
            debit: Number(l.debit) || 0,
            credit: Number(l.credit) || 0,
            description: l.description || '',
          })),
      };
      await api.post('/ledger/journals', payload);
      toast.success(autoPost ? 'Journal created and posted' : 'Journal saved as draft');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-journal-dialog">
        <DialogHeader>
          <DialogTitle>New journal entry</DialogTitle>
          <DialogDescription>Each line debits or credits one account. Total debits must equal total credits.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="je-date" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoPost}
                onChange={(e) => setAutoPost(e.target.checked)}
                className="h-4 w-4"
                data-testid="je-auto-post"
              />
              Post immediately (otherwise saves as draft)
            </label>
          </div>
        </div>
        <div>
          <Label>Memo</Label>
          <Textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="What is this entry for?" data-testid="je-memo" />
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-2 py-2">Account</th>
                <th className="text-right px-2 py-2 w-32">Debit</th>
                <th className="text-right px-2 py-2 w-32">Credit</th>
                <th className="text-left px-2 py-2 w-44">Description</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.uid} className="border-t" data-testid={`je-line-${i}`}>
                  <td className="px-2 py-1.5">
                    <Select value={l.account_id} onValueChange={(v) => setLine(i, { account_id: v })}>
                      <SelectTrigger className="h-9" data-testid={`je-account-${i}`}>
                        <SelectValue placeholder="Choose account" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {accounts.map((a) => (
                          <SelectItem key={a.account_id} value={a.account_id}>
                            {a.code} · {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.debit}
                      onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                      className="text-right tabular-nums h-9"
                      data-testid={`je-debit-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.credit}
                      onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
                      className="text-right tabular-nums h-9"
                      data-testid={`je-credit-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={l.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                      placeholder="Optional"
                      className="h-9"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 2}
                      data-testid={`je-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-semibold">
              <tr>
                <td className="px-2 py-2 text-right">Totals</td>
                <td className="px-2 py-2 text-right tabular-nums" data-testid="je-total-dr">{fmtMoney(totalDr)}</td>
                <td className="px-2 py-2 text-right tabular-nums" data-testid="je-total-cr">{fmtMoney(totalCr)}</td>
                <td className="px-2 py-2 text-xs">
                  {balanced
                    ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Balanced</span>
                    : <span className="text-rose-700">Unbalanced</span>}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <Button variant="outline" onClick={addLine} className="gap-1.5 w-fit" data-testid="je-add-line-btn">
          <Plus className="h-4 w-4" /> Add line
        </Button>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !balanced} className="gap-1.5" data-testid="je-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {autoPost ? 'Post journal' : 'Save draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JournalDetailDialog({ journal, accounts, onClose, onPost, onReverse }) {
  const open = !!journal;
  const j = journal || {};
  const accMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.account_id, a])), [accounts]);
  const totalDr = (j.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="journal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {j.entry_no} <StatusBadge status={j.status} />
          </DialogTitle>
          <DialogDescription>{j.date} · {j.memo}</DialogDescription>
        </DialogHeader>
        {j.lines ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-right px-3 py-2 w-32">Debit</th>
                  <th className="text-right px-3 py-2 w-32">Credit</th>
                  <th className="text-left px-3 py-2 w-44">Description</th>
                </tr>
              </thead>
              <tbody>
                {keyed(j.lines, l => `${l.account_id}-${l.debit || ''}-${l.credit || ''}`).map(({ item: l, key }) => {
                  const acct = accMap[l.account_id] || {};
                  return (
                    <tr key={key} className="border-t">
                      <td className="px-3 py-2"><span className="font-mono mr-2">{l.account_code || acct.code}</span>{l.account_name || acct.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{l.debit ? fmtMoney(l.debit) : ''}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{l.credit ? fmtMoney(l.credit) : ''}</td>
                      <td className="px-3 py-2 text-muted-foreground">{l.description || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/30 font-semibold">
                <tr>
                  <td className="px-3 py-2 text-right">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totalDr)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totalDr)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {j.status === 'draft' && (
            <Button onClick={() => onPost(j.journal_id)} className="gap-1.5" data-testid="je-post-btn">
              <CheckCircle2 className="h-4 w-4" /> Post
            </Button>
          )}
          {j.status === 'posted' && !j.reversed_by && (
            <Button variant="destructive" onClick={() => onReverse(j.journal_id)} className="gap-1.5" data-testid="je-reverse-btn">
              <RotateCcw className="h-4 w-4" /> Reverse
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Trial Balance tab
// ─────────────────────────────────────────────────────────────────────

function TrialBalanceTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [asOf, setAsOf] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ledger/trial-balance', { params: { as_of: asOf } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = { asset: [], liability: [], equity: [], revenue: [], expense: [] };
    for (const r of data?.rows || []) {
      if (out[r.type]) out[r.type].push(r);
    }
    return out;
  }, [data]);

  const totals = data?.totals || { debit: 0, credit: 0, balanced: true };

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Trial Balance</CardTitle>
          <CardDescription>Posted entries up to the as-of date</CardDescription>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>As of</Label>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44" data-testid="tb-as-of" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (data?.rows || []).length === 0 ? (
          <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
            No posted activity in the selected period.
          </div>
        ) : (
          <div className="space-y-6">
            {ACCOUNT_TYPES.map((t) => {
              const rows = grouped[t.value] || [];
              if (rows.length === 0) return null;
              const subDr = rows.reduce((s, r) => s + r.debit, 0);
              const subCr = rows.reduce((s, r) => s + r.credit, 0);
              return (
                <div key={t.value} data-testid={`tb-group-${t.value}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={t.color}>{t.label}</Badge>
                    <span className="text-xs text-muted-foreground">{rows.length} accounts</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2 w-24">Code</th>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-right px-3 py-2 w-32">Debit</th>
                          <th className="text-right px-3 py-2 w-32">Credit</th>
                          <th className="text-right px-3 py-2 w-32">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.account_id} className="border-t" data-testid={`tb-row-${r.code}`}>
                            <td className="px-3 py-2 font-mono">{r.code}</td>
                            <td className="px-3 py-2">{r.name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.debit ? fmtMoney(r.debit) : ''}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.credit ? fmtMoney(r.credit) : ''}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(r.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/30 font-semibold">
                        <tr>
                          <td colSpan={2} className="px-3 py-2 text-right">Subtotal</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(subDr)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(subCr)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              );
            })}

            <div className={`p-4 rounded-lg flex items-center justify-between ${totals.balanced ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
              <div className="text-sm">
                <div className="font-semibold">Grand totals</div>
                <div className="text-xs text-muted-foreground">As of {data?.as_of}</div>
              </div>
              <div className="grid grid-cols-3 gap-6 text-right text-sm">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Debits</div>
                  <div className="font-bold tabular-nums" data-testid="tb-grand-dr">{fmtMoney(totals.debit)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Credits</div>
                  <div className="font-bold tabular-nums" data-testid="tb-grand-cr">{fmtMoney(totals.credit)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Status</div>
                  <div className={`font-bold ${totals.balanced ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="tb-status">
                    {totals.balanced ? 'Balanced' : 'Unbalanced'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Profit & Loss tab
// ─────────────────────────────────────────────────────────────────────

function ProfitLossTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { to };
      if (from) params.from = from;
      const { data } = await api.get('/ledger/profit-loss', { params });
      setData(data);
      if (!from && data?.period?.from) setFrom(data.period.from);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load P&L');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  const s = data?.summary;

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Profit &amp; Loss (Income Statement)</CardTitle>
          <CardDescription>Posted entries between two dates</CardDescription>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="pnl-from" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="pnl-to" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading || !data ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Revenue" value={fmtMoney(s.revenue)} highlight />
              <Stat label="Gross Profit" value={fmtMoney(s.gross_profit)} />
              <Stat label="Operating Profit" value={fmtMoney(s.operating_profit)} />
              <Stat label="Net Profit" value={fmtMoney(s.net_profit)} highlight={s.net_profit >= 0} />
            </div>

            <PLSection title="Revenue" rows={data.revenue.rows} total={data.revenue.total} />

            <div className="rounded-lg border">
              <div className="px-4 py-2 bg-muted/40 font-semibold text-sm">Less: Cost of Goods Sold</div>
              <PLRows rows={data.cogs.rows} />
              <PLTotal label="Total COGS" value={fmtMoney(data.cogs.total)} />
              <PLSubtotal label={`Gross Profit  (${s.gross_margin_pct?.toFixed(1)}% margin)`} value={fmtMoney(s.gross_profit)} />
            </div>

            <div className="rounded-lg border">
              <div className="px-4 py-2 bg-muted/40 font-semibold text-sm">Less: Operating Expenses</div>
              <PLRows rows={data.operating_expenses.rows} />
              <PLTotal label="Total Operating Expenses" value={fmtMoney(data.operating_expenses.total)} />
              <PLSubtotal label="Operating Profit" value={fmtMoney(s.operating_profit)} />
            </div>

            {data.other_expenses.total !== 0 && (
              <div className="rounded-lg border">
                <div className="px-4 py-2 bg-muted/40 font-semibold text-sm">Less: Other Expenses</div>
                <PLRows rows={data.other_expenses.rows} />
                <PLTotal label="Total Other Expenses" value={fmtMoney(data.other_expenses.total)} />
              </div>
            )}

            <div className={`p-4 rounded-lg flex items-center justify-between ${s.net_profit >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Net Profit / (Loss)</div>
                <div className="text-xs text-muted-foreground">{data.period.from} → {data.period.to}</div>
              </div>
              <div className={`text-2xl font-bold tabular-nums ${s.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="pnl-net">
                {fmtMoney(s.net_profit)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PLSection({ title, rows, total }) {
  return (
    <div className="rounded-lg border">
      <div className="px-4 py-2 bg-emerald-50 text-emerald-700 font-semibold text-sm">{title}</div>
      <PLRows rows={rows} />
      <PLTotal label={`Total ${title}`} value={fmtMoney(total)} />
    </div>
  );
}

function PLRows({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="px-4 py-3 text-sm text-muted-foreground italic">No activity</div>;
  }
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.account_id} className="border-t" data-testid={`pl-row-${r.code}`}>
            <td className="px-4 py-1.5 font-mono w-24">{r.code}</td>
            <td className="px-4 py-1.5">{r.name}</td>
            <td className="px-4 py-1.5 text-right tabular-nums w-40">{fmtMoney(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PLTotal({ label, value }) {
  return (
    <div className="flex justify-between px-4 py-2 border-t bg-muted/20 text-sm font-semibold">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function PLSubtotal({ label, value }) {
  return (
    <div className="flex justify-between px-4 py-2 border-t bg-primary/5 text-sm font-bold">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Balance Sheet tab
// ─────────────────────────────────────────────────────────────────────

function BalanceSheetTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [asOf, setAsOf] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ledger/balance-sheet', { params: { as_of: asOf } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load balance sheet');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { load(); }, [load]);
  const s = data?.summary;

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Balance Sheet</CardTitle>
          <CardDescription>Snapshot as of date · Assets must equal Liabilities + Equity</CardDescription>
        </div>
        <div>
          <Label>As of</Label>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44" data-testid="bs-as-of" />
        </div>
      </CardHeader>
      <CardContent>
        {loading || !data ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border">
              <div className="px-4 py-2 bg-emerald-50 text-emerald-700 font-semibold text-sm">Assets</div>
              <PLRows rows={data.assets.rows} />
              <PLTotal label="Total Assets" value={fmtMoney(data.assets.total)} />
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border">
                <div className="px-4 py-2 bg-rose-50 text-rose-700 font-semibold text-sm">Liabilities</div>
                <PLRows rows={data.liabilities.rows} />
                <PLTotal label="Total Liabilities" value={fmtMoney(data.liabilities.total)} />
              </div>
              <div className="rounded-lg border">
                <div className="px-4 py-2 bg-violet-50 text-violet-700 font-semibold text-sm">Equity</div>
                <PLRows rows={data.equity.rows} />
                <PLTotal label="Total Equity" value={fmtMoney(data.equity.total)} />
              </div>
              <div className="rounded-lg border bg-muted/20 px-4 py-2 flex justify-between text-sm font-semibold">
                <span>Liabilities + Equity</span>
                <span className="tabular-nums">{fmtMoney(s.liabilities_and_equity)}</span>
              </div>
            </div>

            <div className={`md:col-span-2 p-4 rounded-lg flex items-center justify-between ${s.balanced ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
              <div className="text-sm">
                <div className="font-semibold">Balance check</div>
                <div className="text-xs text-muted-foreground">As of {data.as_of}</div>
              </div>
              <div className="grid grid-cols-3 gap-6 text-right text-sm">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Assets</div>
                  <div className="font-bold tabular-nums">{fmtMoney(s.assets)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Liab. + Equity</div>
                  <div className="font-bold tabular-nums">{fmtMoney(s.liabilities_and_equity)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Status</div>
                  <div className={`font-bold ${s.balanced ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="bs-status">
                    {s.balanced ? 'Balanced' : 'Out of balance'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Period Close tab
// ─────────────────────────────────────────────────────────────────────

function PeriodCloseTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockDate, setLockDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ledger/fiscal-config');
      setCfg(data);
      setLockDate(data?.lock_through || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load fiscal config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const close = async () => {
    if (!lockDate) {
      toast.error('Pick a lock date first');
      return;
    }
    if (!window.confirm(`Lock all journals dated on or before ${lockDate}? New entries in this range will be rejected.`)) return;
    setSaving(true);
    try {
      await api.post('/ledger/period-close', { lock_through: lockDate });
      toast.success(`Period locked through ${lockDate}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to lock period');
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (!window.confirm('Reopen all closed periods? This removes the lock entirely.')) return;
    setSaving(true);
    try {
      await api.post('/ledger/period-close', { lock_through: null });
      toast.success('Period reopened — all dates writable');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reopen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {cfg?.lock_through ? <Lock className="h-5 w-5 text-rose-600" /> : <Unlock className="h-5 w-5 text-muted-foreground" />}
          Period Close
        </CardTitle>
        <CardDescription>
          Closing a period freezes all journals dated on or before the lock date. Auto-posting from sales, purchases or payroll dated within a closed period will silently fail to keep the books safe.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-6">
            <div className={`p-4 rounded-lg flex items-center justify-between ${cfg?.lock_through ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Current state</div>
                <div className="text-lg font-bold" data-testid="period-state">
                  {cfg?.lock_through ? `Closed through ${cfg.lock_through}` : 'All periods open'}
                </div>
                {cfg?.lock_updated_at && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Last change · {new Date(cfg.lock_updated_at).toLocaleString()}
                  </div>
                )}
              </div>
              {cfg?.lock_through && (
                <Button variant="outline" onClick={reopen} disabled={saving} className="gap-1.5" data-testid="reopen-period-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                  Reopen
                </Button>
              )}
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="font-semibold text-sm">Close period</div>
              <p className="text-xs text-muted-foreground">
                Pick the last date of the period you want to lock — typically the last day of a month after you've reviewed your P&amp;L and Balance Sheet.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label>Lock through (inclusive)</Label>
                  <Input type="date" value={lockDate} onChange={(e) => setLockDate(e.target.value)} className="w-44" data-testid="lock-date" />
                </div>
                <Button onClick={close} disabled={saving || !lockDate} className="gap-1.5" data-testid="close-period-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  Close period
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-4 text-xs text-muted-foreground space-y-1">
              <div><strong>Tenant base currency:</strong> {cfg?.base_currency || 'GHS'}</div>
              <div><strong>Fiscal year start month:</strong> {cfg?.fiscal_year_start_month ?? 1}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

